import { useEffect, useRef } from 'react';
import useSpaceStore from '../stores/spaceStore';
import useEntityStore from '../stores/entityStore';
import { useCommandRegistryStore } from '../stores/commandRegistryStore';
import { createFileLogger } from '../utils/logger';

const log = createFileLogger('TAB_SHORTCUTS', { enabled: true });

/**
 * Registers keyboard shortcuts for browser-like tab and navigation behavior in Electron.
 *
 * Shortcuts are triggered from the native Electron menu (main process) and delivered
 * to the renderer via IPC. This ensures OS-level handling with no keyboard layout
 * or native-menu-conflict issues (e.g. Cmd+W would close the OS window without this).
 *
 * Shortcuts:
 *   Cmd+T          → New tab (opens QuickLauncher, or focuses existing one)
 *   Cmd+W          → Close active tab (pre-focuses adjacent tab first)
 *   Cmd+[          → Navigate back in the active window's entity history
 *   Cmd+]          → Navigate forward in the active window's entity history
 *   Ctrl+Tab       → Switch to next tab
 *   Ctrl+Shift+Tab → Switch to previous tab
 *
 * Only active in Electron (self-gates on window.electron presence).
 *
 * Registration: called once in App.tsx alongside other global shortcut hooks.
 */
export const useGlobalElectronTabShortcuts = () => {
  const { executeCommand } = useCommandRegistryStore();

  // Use a ref so IPC handlers always see the latest executeCommand without
  // needing to re-register listeners on every render.
  const executeCommandRef = useRef(executeCommand);
  useEffect(() => {
    executeCommandRef.current = executeCommand;
  }, [executeCommand]);

  useEffect(() => {
    const electron = (window as any).electron;
    if (!electron?.ipcRenderer) {
      log.log('[TabShortcuts] Not in Electron, skipping IPC listener setup');
      return;
    }

    log.log('[TabShortcuts] Registering Electron tab shortcut IPC listeners');

    // ------------------------------------------------------------------
    // Helpers
    // ------------------------------------------------------------------

    /** Returns the current sorted tab list (same sort as WindowEntityTabs). */
    const getSortedTabs = () => {
      const { windows } = useSpaceStore.getState();
      const { entities } = useEntityStore.getState();

      return Object.values(windows)
        .filter(w => w.type === 'window' && !w.isChildWindow)
        .sort((a, b) => {
          const entityA = a.entityLlmId ? entities.find(e => e.llmId === a.entityLlmId) : null;
          const entityB = b.entityLlmId ? entities.find(e => e.llmId === b.entityLlmId) : null;
          const timeA = new Date((entityA as any)?.updated_at || (entityA as any)?.created_at || 0).getTime();
          const timeB = new Date((entityB as any)?.updated_at || (entityB as any)?.created_at || 0).getTime();
          return timeA - timeB; // oldest first — new tabs appear near the + button
        });
    };

    /** Focuses a tab via the established custom-event pattern. */
    const focusTab = (windowId: string) => {
      window.dispatchEvent(new CustomEvent('windowTabFocus', { detail: { windowId } }));
    };

    /** Closes a tab via the established custom-event pattern. */
    const closeTab = (windowId: string) => {
      window.dispatchEvent(new CustomEvent('windowTabClose', { detail: { windowId } }));
    };

    // ------------------------------------------------------------------
    // Handlers
    // ------------------------------------------------------------------

    const handleNewTab = () => {
      log.log('[TabShortcuts] app:new-tab received');

      const { windows } = useSpaceStore.getState();
      const { entities } = useEntityStore.getState();

      // Replicate plus-button behaviour: focus existing QuickLauncher tab if
      // one is already open, otherwise create a new one.
      const existingNewWindow = Object.values(windows).find(w => {
        const entity = entities.find(e => e._id === w.entityId || e.llmId === w.entityLlmId);
        return entity && (entity as any).entityType === 'Window';
      });

      if (existingNewWindow) {
        log.log('[TabShortcuts] Focusing existing NewWindow tab:', existingNewWindow.id);
        focusTab(existingNewWindow.id);
        return;
      }

      // Derive spaceId from current URL
      let spaceId = 'default';
      const match = window.location.pathname.match(/\/spaces\/([^/]+)/);
      if (match) spaceId = match[1];

      log.log('[TabShortcuts] Creating new tab, spaceId:', spaceId);
      executeCommandRef.current('new_window_create', {
        title: 'New Window',
        spaceId,
        createWindow: true,
      });
    };

    const handleCloseTab = () => {
      log.log('[TabShortcuts] app:close-tab received');

      const { activeWindowId } = useSpaceStore.getState();
      if (!activeWindowId) {
        log.log('[TabShortcuts] No active window to close');
        return;
      }

      const tabs = getSortedTabs();

      // Pre-focus the adjacent tab before closing (same logic as handleTabClose
      // in WindowEntityTabs) so the UI doesn't flash an empty state.
      if (tabs.length > 1) {
        const currentIndex = tabs.findIndex(t => t.id === activeWindowId);
        if (currentIndex !== -1) {
          const nextIndex = currentIndex + 1 < tabs.length ? currentIndex + 1 : currentIndex - 1;
          const nextTab = tabs[nextIndex];
          if (nextTab) {
            log.log('[TabShortcuts] Pre-focusing adjacent tab:', nextTab.id);
            focusTab(nextTab.id);
          }
        }
      }

      log.log('[TabShortcuts] Closing tab:', activeWindowId);
      closeTab(activeWindowId);
    };

    const handleNavigateBack = () => {
      log.log('[TabShortcuts] app:navigate-back received');

      const { activeWindowId, actions } = useSpaceStore.getState();
      if (!activeWindowId) return;

      log.log('[TabShortcuts] Navigating back in window:', activeWindowId);
      (actions as any).navigateBack(activeWindowId).catch((err: Error) => {
        log.log('[TabShortcuts] navigateBack error (likely at history start):', err?.message);
      });
    };

    const handleNavigateForward = () => {
      log.log('[TabShortcuts] app:navigate-forward received');

      const { activeWindowId, actions } = useSpaceStore.getState();
      if (!activeWindowId) return;

      log.log('[TabShortcuts] Navigating forward in window:', activeWindowId);
      (actions as any).navigateForward(activeWindowId).catch((err: Error) => {
        log.log('[TabShortcuts] navigateForward error (likely at history end):', err?.message);
      });
    };

    const handleNextTab = () => {
      log.log('[TabShortcuts] app:next-tab received');

      const { activeWindowId } = useSpaceStore.getState();
      const tabs = getSortedTabs();
      if (tabs.length <= 1) return;

      const currentIndex = tabs.findIndex(t => t.id === activeWindowId);
      const nextIndex = (currentIndex + 1) % tabs.length;
      log.log('[TabShortcuts] Switching to next tab:', tabs[nextIndex].id);
      focusTab(tabs[nextIndex].id);
    };

    const handlePrevTab = () => {
      log.log('[TabShortcuts] app:prev-tab received');

      const { activeWindowId } = useSpaceStore.getState();
      const tabs = getSortedTabs();
      if (tabs.length <= 1) return;

      const currentIndex = tabs.findIndex(t => t.id === activeWindowId);
      const prevIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      log.log('[TabShortcuts] Switching to previous tab:', tabs[prevIndex].id);
      focusTab(tabs[prevIndex].id);
    };

    // ------------------------------------------------------------------
    // Register / unregister
    // ------------------------------------------------------------------

    electron.ipcRenderer.on('app:new-tab', handleNewTab);
    electron.ipcRenderer.on('app:close-tab', handleCloseTab);
    electron.ipcRenderer.on('app:navigate-back', handleNavigateBack);
    electron.ipcRenderer.on('app:navigate-forward', handleNavigateForward);
    electron.ipcRenderer.on('app:next-tab', handleNextTab);
    electron.ipcRenderer.on('app:prev-tab', handlePrevTab);

    log.log('[TabShortcuts] All IPC listeners registered');

    return () => {
      log.log('[TabShortcuts] Removing IPC listeners');
      electron.ipcRenderer.removeListener('app:new-tab', handleNewTab);
      electron.ipcRenderer.removeListener('app:close-tab', handleCloseTab);
      electron.ipcRenderer.removeListener('app:navigate-back', handleNavigateBack);
      electron.ipcRenderer.removeListener('app:navigate-forward', handleNavigateForward);
      electron.ipcRenderer.removeListener('app:next-tab', handleNextTab);
      electron.ipcRenderer.removeListener('app:prev-tab', handlePrevTab);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once — executeCommand is accessed via ref
};
